import {
	applyFactRegisterCommand,
	type FactRegisterChanges,
	type FactRegisterCommand,
	type FactRegisterDocument,
	type FactRegisterFacts,
	type FactRegisterOperation,
	type FactRegisterRow,
} from "../../app/convex/lib/factRegisterCommands";

export type WorkflowFactStatus = "confirmed" | "conflict" | "missing";
export type WorkflowFactConfidence = "high" | "medium" | "low";

export interface WorkflowFactRegisterFact {
	id: string;
	ordinal: number;
	category: string;
	label: string;
	value: string;
	status: WorkflowFactStatus;
	confidence: WorkflowFactConfidence;
	notes?: string;
	sourceDocuments: Array<{
		fileId: string;
		driveId?: string;
		name: string;
		webUrl?: string;
	}>;
}

export interface ParsedWorkflowFactRegister {
	version: number;
	docxWebUrl?: string;
	facts: WorkflowFactRegisterFact[];
}

export interface FactRegisterFold {
	/** Applies one accepted publish call; undefined means stop folding. */
	apply(input: unknown): ParsedWorkflowFactRegister | undefined;
}

export function isAcceptedFactRegisterResult(result: unknown): boolean {
	if (!result || typeof result !== "object" || !("ok" in result)) return false;
	return result.ok === true;
}

function record(value: unknown): Record<string, unknown> | undefined {
	if (!value || typeof value !== "object" || Array.isArray(value)) {
		return undefined;
	}
	return value as Record<string, unknown>;
}

function nonEmptyString(value: unknown): string | undefined {
	return typeof value === "string" && value.length > 0 ? value : undefined;
}

function parseStatus(value: unknown): WorkflowFactStatus | undefined {
	return value === "confirmed" || value === "conflict" || value === "missing"
		? value
		: undefined;
}

function parseConfidence(value: unknown): WorkflowFactConfidence | undefined {
	return value === "high" || value === "medium" || value === "low"
		? value
		: undefined;
}

function parseDocuments(value: unknown): FactRegisterDocument[] | undefined {
	if (!Array.isArray(value)) return undefined;
	const documents: FactRegisterDocument[] = [];
	for (const entry of value) {
		const raw = record(entry);
		if (!raw) return undefined;
		const fileId = nonEmptyString(raw.fileId);
		const name = nonEmptyString(raw.name);
		if (!fileId || !name) return undefined;
		if (raw.driveId != null && typeof raw.driveId !== "string")
			return undefined;
		if (raw.webUrl != null && typeof raw.webUrl !== "string") return undefined;
		if (raw.eTag != null && typeof raw.eTag !== "string") return undefined;
		documents.push({
			fileId,
			...(typeof raw.driveId === "string" ? { driveId: raw.driveId } : {}),
			name,
			...(typeof raw.webUrl === "string" ? { webUrl: raw.webUrl } : {}),
			...(typeof raw.eTag === "string" ? { eTag: raw.eTag } : {}),
		});
	}
	return documents;
}

function parseRow(value: unknown): FactRegisterRow | undefined {
	const raw = record(value);
	if (!raw) return undefined;
	const key = nonEmptyString(raw.key);
	const category = nonEmptyString(raw.category);
	const label = nonEmptyString(raw.label);
	const status = parseStatus(raw.status);
	const confidence = parseConfidence(raw.confidence);
	if (!key || !category || !label || !status || !confidence) return undefined;
	if (typeof raw.value !== "string") return undefined;
	if (raw.notes != null && typeof raw.notes !== "string") return undefined;
	const sourceDocuments = parseDocuments(raw.sourceDocuments);
	if (!sourceDocuments) return undefined;
	return {
		key,
		category,
		label,
		value: raw.value,
		status,
		confidence,
		...(typeof raw.notes === "string" && raw.notes ? { notes: raw.notes } : {}),
		sourceDocuments,
	};
}

function parseRows(value: unknown): FactRegisterRow[] | undefined {
	if (!Array.isArray(value) || value.length === 0) return undefined;
	const rows: FactRegisterRow[] = [];
	for (const entry of value) {
		const row = parseRow(entry);
		if (!row) return undefined;
		rows.push(row);
	}
	return rows;
}

function parseChanges(value: unknown): FactRegisterChanges | undefined {
	const raw = record(value);
	if (!raw) return undefined;
	const changes: FactRegisterChanges = {};
	if (raw.category != null) {
		const category = nonEmptyString(raw.category);
		if (!category) return undefined;
		changes.category = category;
	}
	if (raw.label != null) {
		const label = nonEmptyString(raw.label);
		if (!label) return undefined;
		changes.label = label;
	}
	if (raw.value != null) {
		if (typeof raw.value !== "string") return undefined;
		changes.value = raw.value;
	}
	if (raw.status != null) {
		const status = parseStatus(raw.status);
		if (!status) return undefined;
		changes.status = status;
	}
	if (raw.confidence != null) {
		const confidence = parseConfidence(raw.confidence);
		if (!confidence) return undefined;
		changes.confidence = confidence;
	}
	if (raw.notes != null) {
		if (typeof raw.notes !== "string") return undefined;
		changes.notes = raw.notes;
	}
	if (raw.sourceDocuments != null) {
		const sourceDocuments = parseDocuments(raw.sourceDocuments);
		if (!sourceDocuments) return undefined;
		changes.sourceDocuments = sourceDocuments;
	}
	return changes;
}

function parseOperation(value: unknown): FactRegisterOperation | undefined {
	const raw = record(value);
	if (!raw) return undefined;
	if (raw.op === "add") {
		const fact = parseRow(raw.fact);
		return fact ? { op: "add", fact } : undefined;
	}
	const key = nonEmptyString(raw.key);
	if (!key) return undefined;
	if (raw.op === "remove") return { op: "remove", key };
	if (raw.op !== "update") return undefined;
	const set = parseChanges(raw.set);
	return set ? { op: "update", key, set } : undefined;
}

function parseOperations(value: unknown): FactRegisterOperation[] | undefined {
	if (!Array.isArray(value) || value.length === 0) return undefined;
	const operations: FactRegisterOperation[] = [];
	for (const entry of value) {
		const operation = parseOperation(entry);
		if (!operation) return undefined;
		operations.push(operation);
	}
	return operations;
}

function integerAtLeast(value: unknown, minimum: number): number | undefined {
	return typeof value === "number" &&
		Number.isInteger(value) &&
		value >= minimum
		? value
		: undefined;
}

/** Accepts command inputs and pre-patch `{version, facts}` transcripts. */
export function parseFactRegisterCommandInput(
	value: unknown,
): FactRegisterCommand | undefined {
	const raw = record(value);
	if (!raw) return undefined;
	if (raw.docxWebUrl != null && typeof raw.docxWebUrl !== "string") {
		return undefined;
	}
	const docxWebUrl =
		typeof raw.docxWebUrl === "string" && raw.docxWebUrl
			? { docxWebUrl: raw.docxWebUrl }
			: {};
	if (raw.mode === undefined) {
		const version = integerAtLeast(raw.version, 1);
		const facts = parseRows(raw.facts);
		if (version === undefined || !facts) return undefined;
		return { mode: "replace", baseVersion: version - 1, facts, ...docxWebUrl };
	}
	const baseVersion = integerAtLeast(raw.baseVersion, 0);
	if (baseVersion === undefined) return undefined;
	if (raw.mode === "replace") {
		const facts = parseRows(raw.facts);
		return facts
			? { mode: "replace", baseVersion, facts, ...docxWebUrl }
			: undefined;
	}
	if (raw.mode !== "patch") return undefined;
	const ops = parseOperations(raw.ops);
	return ops ? { mode: "patch", baseVersion, ops, ...docxWebUrl } : undefined;
}

function toDisplayFact(
	row: FactRegisterRow,
	index: number,
): WorkflowFactRegisterFact {
	return {
		id: row.key,
		ordinal: index + 1,
		category: row.category,
		label: row.label,
		value: row.value,
		status: row.status,
		confidence: row.confidence,
		...(row.notes ? { notes: row.notes } : {}),
		sourceDocuments: row.sourceDocuments.map((document) => ({
			fileId: document.fileId,
			name: document.name,
			...(document.driveId ? { driveId: document.driveId } : {}),
			...(document.webUrl ? { webUrl: document.webUrl } : {}),
		})),
	};
}

/** Replays publish commands the server already accepted, in transcript order. */
export function createFactRegisterFold(): FactRegisterFold {
	let state: FactRegisterFacts = { facts: [] };
	return {
		apply(input: unknown): ParsedWorkflowFactRegister | undefined {
			const command = parseFactRegisterCommandInput(input);
			if (!command) return undefined;
			const merged = applyFactRegisterCommand(state, command);
			if (!merged.ok) return undefined;
			state = merged.state;
			return {
				version: command.baseVersion + 1,
				...(state.docxWebUrl ? { docxWebUrl: state.docxWebUrl } : {}),
				facts: state.facts.map(toDisplayFact),
			};
		},
	};
}
